import { FC, useState } from "react";
import { IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import { CheckOutlined, EditOutlined } from "@mui/icons-material";

type Props = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
};

/**
 * Free-text note for a lifecycle, surfaced on the sign-off overview and PDF.
 * Locked by default to reduce accidental edits, unlocked via the toggle.
 * Mirrors the clear contract case field pattern from the Legal lifecycle.
 */
export const SignOffNoteField: FC<Props> = ({
  value,
  onChange,
  label = "Sign-off note"
}) => {
  const [isEditable, setIsEditable] = useState(false);

  const toggleEditing = () => setIsEditable((prev) => !prev);

  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      multiline
      minRows={2}
      disabled={!isEditable}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end" sx={{ alignSelf: "flex-start" }}>
              <Tooltip
                title={
                  isEditable
                    ? "Lock sign-off note"
                    : "Unlock sign-off note for editing"
                }
              >
                <IconButton
                  onClick={toggleEditing}
                  edge="end"
                  color={isEditable ? "secondary" : "primary"}
                >
                  {isEditable ? <CheckOutlined /> : <EditOutlined />}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          )
        }
      }}
      autoFocus={isEditable}
      helperText={
        isEditable
          ? "Editing enabled until you lock it again or save the dialog. Shown on the sign-off overview and PDF."
          : "Locked to reduce accidental changes. Shown on the sign-off overview and PDF."
      }
    />
  );
};
